import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useApp } from "../../contexts/AppContext";
import PageLayout from "../../components/common/PageLayout";
import CustomToast from "../../components/common/CustomToast";
import ControlBar from "./components/ControlBar";
import TabNavigation from "./components/TabNavigation";
import OverviewView from "./Overview";
import TrendView from "./Trend";
import {
  setActiveTab,
  setSelectedPeriodWithDate,
  setSelectedDate,
  setCompletedOnly,
  setTrendPeriod,
  setTrendDateRange,
  fetchDashboardOverview,
  fetchDashboardTrend,
} from "./slice";
import { getTestDataByPeriod, getTrendTestData } from "./testData";
import "./dashboard.css";

// Helper function to calculate trend date range based on selectedPeriod and selectedDate
const calculateTrendDateRange = (period, selectedDate) => {
  if (!selectedDate) {
    // Fallback to default behavior
    const now = new Date();
    const endDate = now.toISOString().split("T")[0];
    let startDate;

    switch (period) {
      case "monthly": {
        const sixMonthsAgo = new Date(now);
        sixMonthsAgo.setMonth(now.getMonth() - 6);
        startDate = sixMonthsAgo.toISOString().split("T")[0];
        break;
      }
      case "weekly": {
        const twelveWeeksAgo = new Date(now);
        twelveWeeksAgo.setDate(now.getDate() - 12 * 7);
        startDate = twelveWeeksAgo.toISOString().split("T")[0];
        break;
      }
      case "quarterly": {
        const oneYearAgo = new Date(now);
        oneYearAgo.setFullYear(now.getFullYear() - 1);
        startDate = oneYearAgo.toISOString().split("T")[0];
        break;
      }
      case "yearly": {
        const threeYearsAgo = new Date(now);
        threeYearsAgo.setFullYear(now.getFullYear() - 3);
        startDate = threeYearsAgo.toISOString().split("T")[0];
        break;
      }
      default:
        startDate = endDate;
    }

    return { startDate, endDate };
  }

  const now = new Date();
  const { year, month, quarter, date } = selectedDate;

  let startDate;
  let normalEndDate;

  switch (period) {
    case "weekly": {
      // selectedDate.date is YYYY-MM-DD
      startDate = date;
      const start = new Date(date);
      start.setMonth(start.getMonth() + 3); // Add 3 months
      normalEndDate = start.toISOString().split("T")[0];
      break;
    }

    case "monthly": {
      // Use YYYY-MM-01 format
      startDate = `${year}-${String(month + 1).padStart(2, "0")}-01`;
      const start = new Date(year, month);
      start.setMonth(start.getMonth() + 6); // Add 6 months
      normalEndDate = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}-01`;
      break;
    }

    case "quarterly": {
      // Quarter starts: Q1=Jan, Q2=Apr, Q3=Jul, Q4=Oct
      const startMonth = (quarter - 1) * 3;
      startDate = `${year}-${String(startMonth + 1).padStart(2, "0")}-01`;
      const start = new Date(year, startMonth);
      start.setMonth(start.getMonth() + 12); // Add 4 quarters (12 months)
      normalEndDate = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}-01`;
      break;
    }

    case "yearly": {
      startDate = `${year}-01-01`;
      normalEndDate = `${year + 4}-01-01`; // Add 4 years
      break;
    }

    default:
      startDate = now.toISOString().split("T")[0];
      normalEndDate = startDate;
  }

  // Check if normalEndDate exceeds current time
  const endDateObj = new Date(normalEndDate);
  let currentPeriodStart;

  switch (period) {
    case "weekly":
      // For weekly, use current date
      currentPeriodStart = now.toISOString().split("T")[0];
      break;
    case "monthly":
      // Current month start
      currentPeriodStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
      break;
    case "quarterly": {
      // Current quarter start
      const currentQuarter = Math.floor(now.getMonth() / 3);
      const currentQuarterMonth = currentQuarter * 3;
      currentPeriodStart = `${now.getFullYear()}-${String(currentQuarterMonth + 1).padStart(2, "0")}-01`;
      break;
    }
    case "yearly":
      // Current year start
      currentPeriodStart = `${now.getFullYear()}-01-01`;
      break;
    default:
      currentPeriodStart = now.toISOString().split("T")[0];
  }

  const currentPeriodStartObj = new Date(currentPeriodStart);

  if (endDateObj > currentPeriodStartObj) {
    // Calculate how much we exceeded
    const startDateObj = new Date(startDate);

    switch (period) {
      case "weekly": {
        // Calculate in days/weeks
        const totalDays = Math.floor(
          (endDateObj - currentPeriodStartObj) / (1000 * 60 * 60 * 24)
        );
        const exceededWeeks = Math.ceil(totalDays / 7);
        const adjustedStart = new Date(startDateObj);
        adjustedStart.setDate(adjustedStart.getDate() - exceededWeeks * 7);
        startDate = adjustedStart.toISOString().split("T")[0];
        break;
      }

      case "monthly": {
        // Calculate exceeded months
        const exceededPeriods =
          (endDateObj.getFullYear() - currentPeriodStartObj.getFullYear()) *
            12 +
          (endDateObj.getMonth() - currentPeriodStartObj.getMonth());
        const adjustedStart = new Date(startDateObj);
        adjustedStart.setMonth(adjustedStart.getMonth() - exceededPeriods);
        startDate = `${adjustedStart.getFullYear()}-${String(adjustedStart.getMonth() + 1).padStart(2, "0")}-01`;
        break;
      }

      case "quarterly": {
        // Calculate exceeded quarters
        const endQuarter = Math.floor(endDateObj.getMonth() / 3);
        const currentQuarter = Math.floor(currentPeriodStartObj.getMonth() / 3);
        const exceededPeriods =
          (endDateObj.getFullYear() - currentPeriodStartObj.getFullYear()) * 4 +
          (endQuarter - currentQuarter);
        const adjustedStart = new Date(startDateObj);
        adjustedStart.setMonth(adjustedStart.getMonth() - exceededPeriods * 3);
        const adjustedQuarterMonth =
          Math.floor(adjustedStart.getMonth() / 3) * 3;
        startDate = `${adjustedStart.getFullYear()}-${String(adjustedQuarterMonth + 1).padStart(2, "0")}-01`;
        break;
      }

      case "yearly": {
        // Calculate exceeded years
        const exceededPeriods =
          endDateObj.getFullYear() - currentPeriodStartObj.getFullYear();
        const adjustedYear = startDateObj.getFullYear() - exceededPeriods;
        startDate = `${adjustedYear}-01-01`;
        break;
      }

      default:
        break;
    }

    // Use current period start as endDate
    return { startDate, endDate: currentPeriodStart };
  }

  // No adjustment needed
  return { startDate, endDate: normalEndDate };
};

const Dashboard = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const {
    device: { isMobile },
  } = useApp();

  // Development flag - set to false for production API
  const USE_TEST_DATA = false;

  const {
    activeTab,
    selectedPeriod,
    selectedDate,
    completedOnly,
    trendPeriod,
    trendStartDate,
    trendEndDate,
  } = useSelector((state) => state.dashboard);

  const [showSuccessToast, setShowSuccessToast] = useState(false);

  // Check for password reset success state from navigation
  useEffect(() => {
    if (location.state?.showPasswordResetSuccess) {
      setShowSuccessToast(true);

      // Clear the state to prevent showing toast on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Data fetching logic - switches between test data and API based on flag
  useEffect(() => {
    if (USE_TEST_DATA) {
      // Development: Use test data
      const loadTestData = () => {
        if (activeTab === "overview") {
          dispatch({ type: "dashboard/fetchOverview/pending" });

          setTimeout(() => {
            const testData = getTestDataByPeriod(selectedPeriod);
            dispatch({
              type: "dashboard/fetchOverview/fulfilled",
              payload: testData,
            });
          }, 500);
        } else if (activeTab === "trend") {
          dispatch({ type: "dashboard/fetchTrend/pending" });

          setTimeout(() => {
            const testData = getTrendTestData(
              trendPeriod,
              trendStartDate,
              trendEndDate
            );
            dispatch({
              type: "dashboard/fetchTrend/fulfilled",
              payload: testData,
            });
          }, 500);
        }
      };

      loadTestData();
    } else {
      // Production: Use API call
      const fetchData = () => {
        // Convert selectedDate to YYYY-MM-DD format for API
        let dateParam = null;
        if (selectedDate) {
          const { year, month, quarter, date } = selectedDate;
          if (selectedPeriod === "monthly" && month !== undefined) {
            dateParam = `${year}-${String(month + 1).padStart(2, "0")}-01`;
          } else if (selectedPeriod === "quarterly" && quarter !== undefined) {
            const quarterMonth = (quarter - 1) * 3;
            dateParam = `${year}-${String(quarterMonth + 1).padStart(2, "0")}-01`;
          } else if (selectedPeriod === "weekly" && date !== undefined) {
            // For weekly, use the date string directly
            dateParam = date;
          } else if (selectedPeriod === "yearly") {
            dateParam = `${year}-01-01`;
          }
        }

        if (activeTab === "overview") {
          dispatch(
            fetchDashboardOverview({
              period: selectedPeriod,
              date: dateParam,
              completedOnly: completedOnly,
            })
          );
        } else if (activeTab === "trend") {
          // For trend, use the trend-specific date range and period
          dispatch(
            fetchDashboardTrend({
              startDate: trendStartDate,
              endDate: trendEndDate,
              period: trendPeriod,
              completedOnly: completedOnly,
            })
          );
        }
      };

      fetchData();
    }
  }, [
    dispatch,
    activeTab,
    selectedPeriod,
    selectedDate,
    completedOnly,
    trendPeriod,
    trendStartDate,
    trendEndDate,
    USE_TEST_DATA,
  ]);

  // Event handlers
  const handleTabChange = (tab) => {
    dispatch(setActiveTab(tab));

    // When switching to trend, calculate date range based on selectedPeriod and selectedDate
    if (tab === "trend") {
      const { startDate, endDate } = calculateTrendDateRange(
        selectedPeriod,
        selectedDate
      );

      // Sync period and date range
      dispatch(setTrendPeriod(selectedPeriod));
      dispatch(setTrendDateRange({ startDate, endDate }));
    }
  };

  const handlePeriodChange = (period) => {
    dispatch(setSelectedPeriodWithDate(period));
  };

  const handleDateChange = (date) => {
    dispatch(setSelectedDate(date));
  };

  const handleCompletedOnlyChange = (checked) => {
    dispatch(setCompletedOnly(checked));
  };

  return (
    <PageLayout
      customHeader={
        <>
          <h2 className="pb-3">Dashboard</h2>
          {/* Tab Navigation */}
          <div className="dashboard-header">
            <TabNavigation
              activeTab={activeTab}
              onTabChange={handleTabChange}
            />
          </div>
        </>
      }
      className="dashboard-container"
    >
      {/* Dashboard Views */}
      <div className="dashboard-views">
        {activeTab === "overview" && (
          <>
            {/* Control Bar for Overview */}
            <ControlBar
              selectedPeriod={selectedPeriod}
              onPeriodChange={handlePeriodChange}
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
              completedOnly={completedOnly}
              onCompletedOnlyChange={handleCompletedOnlyChange}
            />
            <OverviewView />
          </>
        )}
        {activeTab === "trend" && <TrendView />}
      </div>

      {/* Success Toast */}
      <CustomToast
        show={showSuccessToast}
        onClose={() => setShowSuccessToast(false)}
        title="Success"
        message="Password has been reset successfully."
        variant="success"
        position="bottom-end"
        isMobile={isMobile}
        autohide={true}
      />
    </PageLayout>
  );
};

export default Dashboard;
